import { Box, Flex } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useSelectedColours } from 'app/contexts/Branding';

interface SidebarRowProps {
  icon: ReactNode;
  children: ReactNode;
  /** Renders a link when set; otherwise the row is a button. */
  to?: string;
  href?: string;
  isActive?: boolean;
  onClick?: () => void;
  trailing?: ReactNode;
  'data-testid'?: string;
}

// These users need targets they can hit: every row fills the sidebar's width and is at
// least 44px tall, with hover and active states covering the whole row rather than the
// label alone.
const ROW_STYLES = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  w: 'full',
  minH: '46px',
  px: '12px',
  py: '8px',
  borderRadius: '8px',
  textAlign: 'left' as const,
  textStyle: 'smallMedium',
  transition: 'background 0.15s ease, color 0.15s ease',
  _focusVisible: {
    outline: '2px solid',
    outlineColor: 'brand.indigo',
    outlineOffset: '-2px',
  },
};

function SidebarRow({
  icon,
  children,
  to = undefined,
  href = undefined,
  isActive = false,
  onClick = undefined,
  trailing = undefined,
  'data-testid': testId = undefined,
}: SidebarRowProps) {
  const selected = useSelectedColours();
  const rowProps = {
    ...ROW_STYLES,
    onClick,
    'data-testid': testId,
    bg: isActive ? selected.fill : 'transparent',
    color: isActive ? selected.text : 'brand.black',
    _hover: {
      bg: isActive ? selected.fill : 'brand.navMuted',
      textDecoration: 'none',
      color: isActive ? selected.text : 'brand.black',
    },
    _active: { bg: isActive ? selected.fill : 'brand.mediumPurple' },
  };

  const content = (
    <>
      {/* Purple icon on an inactive row, white once the row is the active pill. */}
      <Flex
        flexShrink={0}
        align="center"
        color={isActive ? selected.text : selected.icon}
        aria-hidden="true"
      >
        {icon}
      </Flex>
      <Box flex="1" minW="0">
        {children}
      </Box>
      {trailing}
    </>
  );

  if (to) {
    return (
      <Flex {...rowProps} as={RouterLink} to={to} aria-current={isActive ? 'page' : undefined}>
        {content}
      </Flex>
    );
  }

  if (href) {
    return (
      <Flex {...rowProps} as="a" href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </Flex>
    );
  }

  return (
    <Flex {...rowProps} as="button" type="button">
      {content}
    </Flex>
  );
}

export default SidebarRow;
